/**
 * Crew cuts — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_named_haircuts`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const crewCutsPack: ToilePack = {
    slug: 'crew-cuts',
    category: 'beauty',
    name: 'Crew cuts',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'classic', label: 'Classic crew', subject: 'a classic crew cut, short tapered sides and a short top brushed up at the front'},
        {key: 'ivy', label: 'Ivy league', subject: 'an ivy league cut with a side part and a slightly longer top'},
        {key: 'textured', label: 'Textured crew', subject: 'a textured crew cut with a messy short top'},
    ],
    styles: [
        {key: 'office', label: 'Office', style: 'a work portrait in a bright open office, window daylight from camera-left, a clean moderate balance with soft shade on the far cheek', palette: 'A white shirt and a navy blazer, the office blurred in pale grey and glass', framing: 'chest-up, three-quarter turn, eye-level'},
        {key: 'school', label: 'Picture day', style: 'a school picture-day portrait against a plain mottled blue cloth, soft daylight from camera-front, a bright even balance with a faint shadow under the chin', palette: 'Mottled blue behind, a navy crew-neck jumper over a white collar', framing: 'head and shoulders, centred, facing the camera'},
        {key: 'side', label: 'Side view', style: 'a barber lookbook profile against a plain pale-grey wall, daylight from camera-right, a moderate balance with soft shade behind the ear', palette: 'Pale grey and white, a grey T-shirt at the collar', framing: 'head turned in full profile, eye-level'},
    ],
    directions: ['for a teenager', 'grey hair', 'with glasses'],
    directionHint: 'try: for a teenager',
    seo: {
        title: 'Crew Cuts: Classic, Ivy League, Textured',
        metaDescription: 'Nine pictures of the crew cut, short and tidy: a classic, an ivy league and a textured version, at the office, on picture day and in profile.',
        h1: 'Crew cuts: classic, ivy league, textured',
        tagline: 'Nine pictures, three crew cuts, each in an office, on picture day and in profile.',
        intro: 'A crew cut is short and tidy. These pictures show a classic crew with tapered sides and a short top brushed up at the front, an ivy league with a side part and a slightly longer top, and a textured crew with a messy short top. Each is seen in an office, on a school picture day and in profile. You can type a change, such as grey hair, to ask for the cut with it.',
        useCases: [
            'a tidy cut for work or school',
            'choosing between a classic, ivy league and textured crew',
            'a reference for the barber',
            'a profile picture',
        ],
        faq: [
            {q: 'What is the difference between a classic crew and an ivy league?', a: 'The classic crew has short tapered sides and a short top brushed up at the front. The ivy league has a side part and a slightly longer top.'},
            {q: 'How is the textured crew different from the classic?', a: 'It keeps the same short shape with a messy top. The classic is combed up neatly at the front.'},
            {q: 'Can I ask for a different age or look?', a: 'Yes. Type the change and the picture is made with it. It is a request, so the result may differ from the sample.'},
        ],
    },
}
